import { useEffect, useRef, useState, FormEvent } from 'react';

const FORM_URL = 'https://readdy.ai/api/form/d70cn4mth28qd804cosg';

const ContactSection = () => {
  const [visible, setVisible] = useState(false);
  const [sending, setSending] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const ref = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.05 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSending(true);
    setError('');
    const form = e.currentTarget;
    const data = new FormData(form);
    const params = new URLSearchParams();
    data.forEach((value, key) => params.append(key, value.toString()));
    try {
      const res = await fetch(FORM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params.toString(),
      });
      if (res.ok) {
        setSubmitted(true);
        form.reset();
      } else {
        setError('送信に失敗しました。時間をおいて再度お試しください。');
      }
    } catch {
      setError('送信中にエラーが発生しました。');
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contact" ref={ref} className="py-20 md:py-28 bg-[#F8F9F7]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        {/* Header */}
        <div className={`text-center mb-14 transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <span className="text-[#B2AC88] text-xs font-medium tracking-widest uppercase">Contact</span>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-[#2F3630] mt-3 mb-4" style={{ fontFamily: "'Shippori Mincho', serif" }}>
            お問い合わせ
          </h2>
          <p className="text-gray-500 text-sm sm:text-base">
            岡山市の高価買取・査定についてお気軽にご相談ください
          </p>
        </div>

        <div className={`grid grid-cols-1 lg:grid-cols-2 gap-8 transition-all duration-700 delay-100 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          {/* Left: Phone & Info */}
          <div className="flex flex-col gap-6">
            {/* Phone Card */}
            <div className="bg-[#B2AC88] rounded-2xl p-7 md:p-10 text-white">
              <p className="text-white/70 text-xs font-medium tracking-widest uppercase mb-3">電話でのお問い合わせ</p>
              <a href="tel:086-238-7864" className="flex items-center gap-3 mb-2 cursor-pointer group">
                <div className="w-10 h-10 flex items-center justify-center bg-white/20 rounded-full">
                  <i className="ri-phone-fill text-white text-lg" />
                </div>
                <span className="text-3xl md:text-4xl font-bold tracking-wide group-hover:text-white/80 transition-colors whitespace-nowrap">
                  086-238-7864
                </span>
              </a>
              <p className="text-white/80 text-sm">営業時間：9:00〜17:00（土曜・日曜・祝日を除く）</p>
            </div>

            {/* Store Info */}
            <div className="bg-white rounded-2xl p-7 md:p-8">
              <p className="text-gray-400 text-xs font-medium tracking-widest uppercase mb-4">店舗</p>
              <div className="flex items-start gap-3 mb-4">
                <div className="w-9 h-9 flex items-center justify-center bg-[#B2AC88]/15 rounded-lg flex-shrink-0 mt-0.5">
                  <i className="ri-map-pin-line text-[#B2AC88] text-base" />
                </div>
                <div>
                  <p className="text-gray-800 font-medium text-sm leading-relaxed">
                    〒703-8271<br />
                    岡山県岡山市中区倉田652番地15
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3 mb-6">
                <div className="w-9 h-9 flex items-center justify-center bg-[#B2AC88]/15 rounded-lg flex-shrink-0">
                  <i className="ri-time-line text-[#B2AC88] text-base" />
                </div>
                <p className="text-gray-800 text-sm">
                  9:00〜17:00 &nbsp;|&nbsp; <span className="text-red-500">定休日：土曜・日曜・祝日</span>
                </p>
              </div>

            </div>
          </div>

          {/* Right: Form */}
          <div className="bg-white rounded-2xl p-7 md:p-10">
            <p className="text-gray-400 text-xs font-medium tracking-widest uppercase mb-6">お問い合わせフォーム</p>

            {submitted ? (
              <div className="flex flex-col items-center justify-center text-center py-16 gap-4">
                <div className="w-16 h-16 flex items-center justify-center bg-[#B2AC88]/15 rounded-full">
                  <i className="ri-checkbox-circle-line text-[#B2AC88] text-3xl" />
                </div>
                <p className="text-[#2F3630] font-bold text-lg">送信完了しました</p>
                <p className="text-gray-500 text-sm leading-relaxed">
                  お問い合わせありがとうございます。<br />
                  担当スタッフより翌営業日中にご連絡いたします。
                </p>
                <button
                  onClick={() => setSubmitted(false)}
                  className="mt-2 text-[#B2AC88] text-sm underline cursor-pointer whitespace-nowrap"
                >
                  別のお問い合わせをする
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} data-readdy-form action={FORM_URL} method="POST">
                <div className="flex flex-col gap-5">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div>
                      <label className="block text-gray-700 text-xs font-medium mb-1.5">
                        お名前 <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        name="name"
                        required
                        placeholder="山田 太郎"
                        className="w-full border border-gray-200 rounded-lg px-4 py-2.5 text-sm text-gray-800 placeholder-gray-300 focus:outline-none focus:border-[#B2AC88] transition-colors"
                      />
                    </div>
                    <div>
                      <label className="block text-gray-700 text-xs font-medium mb-1.5">
                        メールアドレス <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="email"
                        name="email"
                        required
                        placeholder="example@mail.com"
                        className="w-full border border-gray-200 rounded-lg px-4 py-2.5 text-sm text-gray-800 placeholder-gray-300 focus:outline-none focus:border-[#B2AC88] transition-colors"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-gray-700 text-xs font-medium mb-1.5">電話番号</label>
                    <input
                      type="tel"
                      name="phone"
                      placeholder="086-000-0000"
                      className="w-full border border-gray-200 rounded-lg px-4 py-2.5 text-sm text-gray-800 placeholder-gray-300 focus:outline-none focus:border-[#B2AC88] transition-colors"
                    />
                  </div>
                  <div>
                    <label className="block text-gray-700 text-xs font-medium mb-1.5">お問い合わせ種別</label>
                    <select
                      name="type"
                      className="w-full border border-gray-200 rounded-lg px-4 py-2.5 text-sm text-gray-800 focus:outline-none focus:border-[#B2AC88] transition-colors bg-white"
                    >
                      <option value="">選択してください</option>
                      <option value="出張買取">出張買取</option>
                      <option value="宅配買取">宅配買取</option>
                      <option value="査定について">査定について</option>
                      <option value="その他">その他</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-gray-700 text-xs font-medium mb-1.5">
                      ご相談内容 <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      name="message"
                      required
                      maxLength={500}
                      rows={5}
                      placeholder="買取をご希望の品物や、ご不明な点などをご記入ください。（500文字以内）"
                      className="w-full border border-gray-200 rounded-lg px-4 py-2.5 text-sm text-gray-800 placeholder-gray-300 focus:outline-none focus:border-[#B2AC88] transition-colors resize-none"
                    />
                  </div>

                  {error && (
                    <p className="text-red-500 text-xs">{error}</p>
                  )}

                  <button
                    type="submit"
                    disabled={sending}
                    className="w-full flex items-center justify-center gap-2 bg-[#D97706] hover:bg-[#c06a05] disabled:bg-gray-300 text-white font-bold py-3.5 rounded-full transition-colors duration-200 text-sm whitespace-nowrap"
                  >
                    {sending ? (
                      <>
                        <i className="ri-loader-4-line animate-spin" />
                        送信中...
                      </>
                    ) : (
                      <>
                        <i className="ri-send-plane-line" />
                        無料査定を申し込む
                      </>
                    )}
                  </button>

                  <p className="text-gray-400 text-xs text-center">
                    送信いただいた個人情報は、お問い合わせへの対応のみに使用いたします。
                  </p>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
